import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Wallet, RefreshCw, Lock, WifiOff, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const Login = () => {
  const { signInWithGoogle, signInAsGuest, currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Redirect to transactions if user is already logged in
  useEffect(() => {
    if (currentUser) {
      console.log('User already logged in, redirecting to transactions');
      navigate('/transactions');
    }
  }, [currentUser, navigate]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      await signInWithGoogle();
      // After redirect, user will be automatically redirected by useEffect
    } catch (error) {
      console.error('Sign in failed:', error);
      toast.error('Sign in failed. Please try again.');
      setLoading(false);
    }
  };

  const handleGuestSignIn = () => {
    signInAsGuest();
    toast.success('Welcome to Spend Easy Demo Mode!');
    navigate('/transactions');
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4" 
      style={{ 
        background: 'linear-gradient(135deg, #F8F9FA 0%, #FFFFFF 100%)' 
      }}
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {/* App Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4" style={{ backgroundColor: '#E8544F' }}>
            <Wallet className="w-10 h-10 text-white" />
          </div>
          
          {/* App Name */}
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#212529' }}>
            Spend Easy
          </h1>
          
          {/* Tagline */}
          <p className="text-base" style={{ color: '#6C757D' }}>
            Track your money, effortlessly
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col items-center gap-3 mb-10">
          <Button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full max-w-xs h-12 bg-white text-gray-700 font-semibold border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-3 transition-all duration-200"
            style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)' }}
          >
            {loading ? (
              <span>Signing in...</span>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Continue with Google</span>
              </>
            )}
          </Button>

          <div className="flex items-center gap-3 w-full max-w-xs my-1">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs uppercase tracking-wider text-gray-400 font-medium">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <Button
            onClick={handleGuestSignIn}
            disabled={loading}
            className="w-full max-w-xs h-12 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-all duration-200 hover:opacity-95"
            style={{ backgroundColor: '#E8544F', boxShadow: '0 4px 12px rgba(232, 84, 79, 0.3)' }}
          >
            <Sparkles className="w-5 h-5 text-white" />
            <span>Explore Demo / Guest Mode</span>
          </Button>
          <p className="text-xs text-center text-gray-400">
            No Google account required. Try out all features instantly!
          </p>
        </div>

        {/* Feature Highlights */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
              <RefreshCw className="w-5 h-5" style={{ color: '#5B9FE3' }} />
            </div>
            <span style={{ color: '#6C757D' }}>Sync across devices</span>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
              <Lock className="w-5 h-5" style={{ color: '#4CAF50' }} />
            </div>
            <span style={{ color: '#6C757D' }}>Secure & Private</span>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <WifiOff className="w-5 h-5" style={{ color: '#6C757D' }} />
            </div>
            <span style={{ color: '#6C757D' }}>Works offline</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
